/**
 * How this person checks in and out — HR's decision, on their file.
 *
 * By where they work, their job, and whether they enter restricted areas,
 * HR allows a fingerprint reader, a face recognition reader, the phone at a
 * designated location, or a combination; for the phone, which locations; and
 * says why. The platform enforces it: a phone check-in, or a reader event,
 * by a method this person is not set to use is refused. The person is told
 * by SMS and email when it changes.
 */
import { useState } from "react";
import { useHrEditPerson, useHrGeofences } from "../../api/queries";
import { Button, Card, Field, Input, Select } from "../../design/components";
import type { HrEmployee } from "../../domain/hr";
import { POLICY_BASES, POLICY_METHODS, type PolicyMethod } from "../../domain/hrClock";
import { t } from "../../i18n";
import { Chip, h, niceDate } from "./kit";
import { s } from "./shared";

export function ClockPolicy({ e, canEdit }: { e: HrEmployee; canEdit: boolean }) {
  const fences = useHrGeofences();
  const edit = useHrEditPerson(e.id);
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState<PolicyMethod[]>(e.clockMethods ?? []);
  const [places, setPlaces] = useState<string[] | null>(e.clockGeofenceIds ?? null);
  const [basis, setBasis] = useState(e.clockBasis ?? "");
  const decided = !!e.clockMethods?.length;
  const names = new Map((fences.data ?? []).map((f) => [f.id, f.name]));
  const toggle = (m: PolicyMethod) =>
    setMethods(methods.includes(m) ? methods.filter((x) => x !== m) : [...methods, m]);
  const togglePlace = (id: string) => {
    const now = places ?? [];
    const next = now.includes(id) ? now.filter((x) => x !== id) : [...now, id];
    setPlaces(next.length ? next : null);
  };

  return (
    <Card
      title={t("How they check in and out")}
      sub={t("Set by HR. Any other method is refused.")}
      actions={
        canEdit && !open ? (
          <Button onClick={() => setOpen(true)} data-testid="hr-policy-edit">
            ✎ {t("Change")}
          </Button>
        ) : undefined
      }
      testId="hr-policy"
    >
      {!open ? (
        <>
          {decided ? (
            <div className={h.fileMeta} data-testid="hr-policy-methods">
              {POLICY_METHODS.filter((m) => e.clockMethods?.includes(m.key)).map((m) => (
                <Chip key={m.key} colour="var(--green)">
                  {m.icon} {t(m.label)}
                </Chip>
              ))}
            </div>
          ) : (
            <p className={s.note}>
              {t("Not set yet. Until HR decides, readers work and phones don't.")}
            </p>
          )}
          {e.clockMethods?.includes("phone_location") && (
            <p className={s.dim} style={{ marginTop: 8 }}>
              📍{" "}
              {e.clockGeofenceIds?.length
                ? t("By phone at: {places}", { places: e.clockGeofenceIds.map((id) => names.get(id) ?? id).join(", ") })
                : t("By phone at any designated location")}
            </p>
          )}
          {e.clockBasis && (
            <p style={{ marginTop: 8 }}>
              <b>{t("Why")}:</b> {t(e.clockBasis)}
            </p>
          )}
          {e.clockSetAt && <p className={s.dim}>{t("Set on {d}", { d: niceDate(e.clockSetAt) })}</p>}
        </>
      ) : (
        <form
          className={s.dialogForm}
          onSubmit={(ev) => {
            ev.preventDefault();
            edit.mutate(
              {
                clockMethods: methods.length ? methods : null,
                clockGeofenceIds: methods.includes("phone_location") ? places : null,
                clockBasis: basis.trim() || null,
              },
              { onSuccess: () => setOpen(false) },
            );
          }}
        >
          <div className={h.list}>
            {POLICY_METHODS.map((m) => (
              <label key={m.key} className={h.item} style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={methods.includes(m.key)}
                  onChange={() => toggle(m.key)}
                  data-testid={`hr-policy-${m.key}`}
                />
                <span style={{ fontSize: 20 }} aria-hidden="true">
                  {m.icon}
                </span>
                <span className={h.itemMain}>
                  <b>{t(m.label)}</b>
                  <span>{t(m.when)}</span>
                </span>
              </label>
            ))}
          </div>
          {methods.includes("phone_location") && (
            <fieldset className={s.fieldset}>
              <legend>{t("Where they may check in by phone")}</legend>
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={places == null}
                  onChange={() => setPlaces(places == null ? [] : null)}
                />
                {t("Any designated location")}
              </label>
              {places != null &&
                (fences.data ?? []).map((f) => (
                  <label key={f.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input type="checkbox" checked={places.includes(f.id)} onChange={() => togglePlace(f.id)} />
                    {f.name} <span className={s.dim}>({t("{m} m", { m: f.radiusM })})</span>
                  </label>
                ))}
            </fieldset>
          )}
          <div className={s.form}>
            <Field label="Why (the reason on the file)">
              <Select
                value={POLICY_BASES.includes(basis) ? basis : ""}
                onChange={(ev) => ev.target.value && setBasis(ev.target.value)}
              >
                <option value="">{t("Choose a common reason…")}</option>
                {POLICY_BASES.map((b) => (
                  <option key={b} value={b}>
                    {t(b)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Or in your own words">
              <Input value={basis} onChange={(ev) => setBasis(ev.target.value)} data-testid="hr-policy-basis" />
            </Field>
          </div>
          {methods.length === 0 && (
            <p className={s.note}>{t("With nothing checked, readers work and phones don't.")}</p>
          )}
          <p className={s.note}>{t("An SMS and email about the change is queued for the person.")}</p>
          {edit.error && (
            <p className={s.err}>{edit.error instanceof Error ? edit.error.message : String(edit.error)}</p>
          )}
          <div className={s.formFoot}>
            <Button type="submit" variant="primary" disabled={edit.isPending} data-testid="hr-policy-save">
              {t("Save")}
            </Button>
            <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
          </div>
        </form>
      )}
    </Card>
  );
}
